import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { sendTestWorkerCommand, TEST_WORKER_URL } from '../../runtime/mobile/testWorkerClient';
import { readableMobileError } from '../../runtime/mobile/employeeRuntimeState';
import { colors, radius, spacing, typography } from '../../config/theme';
import { Chip, ErrorBanner, Field, PrimaryButton, TextButton } from '../ui';

// Mid-shift handover: the employee currently on shift hands it to a colleague.
// Cash count + held-receipts check are captured here (they gate the handover
// server-side), then the colleague confirms from their own phone.
export default function HandoverSheet({ user, shift, onClose }) {
  const [candidates, setCandidates] = useState(null); // null = loading
  const [toId, setToId] = useState(null);
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState('');
  const [receipts, setReceipts] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = await user.getIdToken();
        const res = await fetch(`${TEST_WORKER_URL}/shifts/handover-candidates`, {
          method: 'POST',
          headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
          body: JSON.stringify({ shiftId: shift.id }),
        });
        const data = await res.json().catch(() => ({}));
        if (alive) setCandidates(Array.isArray(data.candidates) ? data.candidates : []);
      } catch {
        if (alive) { setCandidates([]); setError('Не удалось загрузить список сменщиков'); }
      }
    })();
    return () => { alive = false; };
  }, [user, shift.id]);

  const missing = useMemo(() => {
    const m = [];
    if (!toId) m.push('сменщика');
    if (!reason.trim()) m.push('причину');
    if (!(amount.trim() && Number.isFinite(Number(amount)) && Number(amount) >= 0)) m.push('сумму в кассе');
    if (!receipts) m.push('отметку про отложенные чеки');
    return m;
  }, [toId, reason, amount, receipts]);

  const submit = async () => {
    if (missing.length) { setError(`Заполни: ${missing.join(', ')}`); return; }
    setBusy(true);
    setError(null);
    try {
      await sendTestWorkerCommand({
        user,
        path: '/commands/request-handover',
        body: {
          shiftId: shift.id,
          toEmployeeId: toId,
          reason: reason.trim(),
          amount: Number(amount),
          heldReceiptsChecked: true,
          heldReceiptsNote: note.trim() || null,
        },
        idempotencyKey: `emp:${user.uid}:handover:${shift.id}:${Date.now()}`,
      });
      onClose(true);
    } catch (e) {
      setError(readableMobileError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal visible animationType="slide" transparent onRequestClose={() => onClose(false)}>
      <View style={styles.wrap}>
        <View style={styles.sheet}>
          <View style={styles.head}>
            <Text style={styles.title}>Передать смену</Text>
            <TextButton title="Отмена" kind="muted" onPress={() => onClose(false)} />
          </View>

          <ScrollView contentContainerStyle={{ gap: spacing.md }} keyboardShouldPersistTaps="handled">
            <ErrorBanner message={error} />

            <View>
              <Text style={styles.label}>КОМУ ПЕРЕДАЁШЬ</Text>
              {candidates === null && <Text style={styles.dim}>Загружаем…</Text>}
              {candidates?.length === 0 && <Text style={styles.dim}>На этой точке больше нет активных сотрудников. Пусть админ назначит сменщика.</Text>}
              {candidates?.map((c) => (
                <Pressable key={c.uid} onPress={() => setToId(c.uid)} style={[styles.pick, toId === c.uid && styles.pickOn]}>
                  <Text style={styles.pickName}>{c.fullName}</Text>
                  {toId === c.uid ? <Chip label="выбран" kind="good" /> : null}
                </Pressable>
              ))}
            </View>

            <Field label="Причина" value={reason} onChangeText={setReason} placeholder="Стало плохо / срочно нужно уйти" />

            <Field label="Сумма в кассе, ₽ (пересчёт)" value={amount} onChangeText={setAmount} keyboardType="decimal-pad" placeholder="0" />

            <Pressable onPress={() => setReceipts((v) => !v)} style={[styles.check, receipts && styles.checkOn]}>
              <View style={[styles.box, receipts && styles.boxOn]}>{receipts ? <Text style={styles.tick}>✓</Text> : null}</View>
              <Text style={styles.checkLabel}>Отложенные чеки проверены</Text>
            </Pressable>

            <Field label="Заметка сменщику (необязательно)" value={note} onChangeText={setNote} placeholder="Что важно знать" multiline />

            <PrimaryButton title="Передать смену" onPress={submit} loading={busy} disabled={missing.length > 0} />
            <Text style={styles.hint}>Сменщик подтвердит приём со своего телефона. До этого смена остаётся на тебе.</Text>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  wrap: { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'flex-end' },
  sheet: { backgroundColor: colors.surface, borderTopLeftRadius: radius.lg, borderTopRightRadius: radius.lg, padding: spacing.lg, maxHeight: '90%' },
  head: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: spacing.md },
  title: { ...typography.h3, color: colors.textPrimary },
  label: { ...typography.caption, color: colors.textMuted, fontWeight: '800', letterSpacing: 1, marginBottom: 6 },
  dim: { ...typography.caption, color: colors.textMuted },
  pick: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border, borderRadius: radius.sm, paddingHorizontal: spacing.md, paddingVertical: spacing.md, marginBottom: 8 },
  pickOn: { borderColor: colors.primary },
  pickName: { ...typography.body, color: colors.textPrimary, fontSize: 14 },
  check: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: colors.surfaceCard, borderWidth: 1, borderColor: colors.border, borderRadius: radius.sm, paddingHorizontal: spacing.md, paddingVertical: spacing.md },
  checkOn: { borderColor: colors.primary },
  box: { width: 22, height: 22, borderRadius: 7, borderWidth: 1.5, borderColor: colors.textMuted, alignItems: 'center', justifyContent: 'center' },
  boxOn: { backgroundColor: colors.primary, borderColor: colors.primary },
  tick: { color: colors.onPrimary, fontWeight: '900', fontSize: 13 },
  checkLabel: { ...typography.body, color: colors.textPrimary, fontSize: 14, flex: 1 },
  hint: { ...typography.caption, color: colors.textMuted },
});
